import { Category, QuestionItem } from "../types/form";
import { evaluateVisibility } from "./formVisibility";
import { isEmptyAnswer } from "./answerEmpty";

export interface CategoryValidationMiss {
  /** The question that needs an answer. */
  question: QuestionItem;
  /** The key under which the answer is stored in formData. */
  inputName: string;
  /** A short, user-facing description of the question (label / questionText). */
  label: string;
}

/**
 * Flatten a category tree node into a list of its questions, recursing into
 * subcategories. Mirrors the structure StepperForm renders into a section.
 */
function collectCategoryQuestions(category: Category): QuestionItem[] {
  const out: QuestionItem[] = [...(category.questions ?? [])];
  for (const sub of category.subcategories ?? []) {
    out.push(...collectCategoryQuestions(sub));
  }
  return out;
}

/**
 * Compute the input-name (formData key) for a question. Matches the
 * convention used in StepperForm: `question.name` when present, otherwise
 * `q_${id}`. Keep in sync if that convention changes.
 */
export function questionInputName(question: QuestionItem): string {
  return question.name ?? `q_${question.id}`;
}

/**
 * Find the visible, non-`info` questions in this section whose answer is
 * empty. Used by "Save & Next" to surface inline validation errors before
 * navigating away.
 *
 * Semantic matches the backend submission gate (see Portal-Backend
 * formReadiness.service.js): visible AND non-info AND non-empty = required.
 *
 * @param activeCategory  The section the user is currently on.
 * @param formData        The full form's in-memory answer map.
 */
export function findMissingInCategory(
  activeCategory: Category | null,
  formData: Record<string, any>,
  visibleIds?: Set<number>,
): CategoryValidationMiss[] {
  if (!activeCategory) return [];

  const misses: CategoryValidationMiss[] = [];
  for (const question of collectCategoryQuestions(activeCategory)) {
    if ((question.answerType ?? question.type) === "info") continue;
    if (!evaluateVisibility(question, formData, visibleIds)) continue;

    const inputName = questionInputName(question);
    const value = formData[inputName];
    if (!isEmptyAnswer(value)) continue;

    misses.push({
      question,
      inputName,
      label: question.label || question.questionText || `Question ${question.id}`,
    });
  }
  return misses;
}
